import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Select, Spin } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

const InlineEditableText: FC<any> = ({
	value,
	onChange,
	inputType = 'text',
	selectOptions = [],
}): React.ReactElement => {
	const [input, setInput] = useState(value);
	const [editMode, setEditMode] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setInput(value);
	}, [value]);

	const onInputChange = useCallback((val) => setInput(val), []);

	const onNumberUnitChange = useCallback((field, val) => {
		setInput((prev) => ({ ...prev, [field]: val }));
	}, []);

	const _enableEditMode = useCallback(() => setEditMode(true), []);
	const _disableEditMode = useCallback(() => setEditMode(false), []);

	const onValueChange = useCallback(() => {
		setLoading(true);
		onChange(input).finally(() => {
			setLoading(false);
			_disableEditMode();
		});
	}, [_disableEditMode, input, onChange]);

	const inputField: React.ReactElement = useMemo(() => {
		switch (inputType) {
			case 'number':
				return <InputNumber value={input} onChange={onInputChange} />;
			case 'numberWithUnit':
				return (
					<div className="flex flex-row items-center">
						<InputNumber
							value={input.number}
							onChange={(val) => onNumberUnitChange('number', val)}
							precision={0}
							step={1}
							style={{ width: 120 }}
						/>
						<Select
							value={input.unit}
							onChange={(val) => onNumberUnitChange('unit', val)}
							style={{ width: 120, marginLeft: 8 }}
						>
							{selectOptions.map((option) => (
								<Select.Option key={option.value} value={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					</div>
				);
			default:
				return <Input value={input} onChange={onInputChange} />;
		}
	}, [input, inputType, onInputChange, onNumberUnitChange, selectOptions]);

	const displayValue = useMemo(() => {
		if (inputType === 'numberWithUnit') {
			if (value && value.number !== null && value.number !== undefined) {
				const unitLabel = selectOptions.find((opt) => opt.value === value.unit);
				return `${value.number} ${unitLabel ? unitLabel.label : value.unit || ''}`;
			}
			return '--';
		}
		return value;
	}, [value, inputType, selectOptions]);

	return editMode ? (
		<div className="flex flex-row items-center">
			<div>{inputField}</div>
			{loading ? (
				<div className="ml-1">
					<Spin />
				</div>
			) : (
				<>
					<div className="ml-1">
						<Button onClick={onValueChange} type="link" icon={<CheckOutlined />} />
					</div>
					<div className="ml-1">
						<Button onClick={_disableEditMode} type="link" icon={<CloseOutlined />} />
					</div>
				</>
			)}
		</div>
	) : (
		<div className="flex flex-row items-center">
			<div>{displayValue}</div>
			<div className="ml-1">
				<Button onClick={_enableEditMode} type="link" icon={<EditOutlined />} />
			</div>
		</div>
	);
};

export default InlineEditableText;
